<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { computed, inject, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { EventBus, Notify, useDialogPluginComponent } from 'quasar';

import { breakpoints } from '@/composables/useBreakpoints.ts';
import { copyToClipboardWithNotification } from '@/helpers/clipboardUtils.ts';
import { useHotKeys } from '@/composables/hotKeys/useHotkeys.ts';
import { HotKeyActionId } from '@/composables/hotKeys/types';
import { FILE_DIALOG_HOT_KEYS } from '@/composables/hotKeys/pageHotKeys';
import {
  getAccountingStatusColorClass,
  getAccountingStatusText,
  getRejectionReasonText,
} from '@/components/ManagementAccountingFileStatus/types.ts';
import { useMeritIntegration } from '@/components/MeritIntegation/useMeritIntegration';
import MeritStatusChip from '@/components/MeritIntegation/MeritStatusChip.vue';
import { useFileStatusChange } from '@/components/ManagementAccountingFileStatus/useAccountingFileStatusChange.ts';

import { EventBusEvents, type typesSubfoldersForQuickMove } from '@/helpers/types.ts';
import {
  AccountingFileType,
  AccountingReportPartTypeEnum,
  FileAccStatus,
  PeriodsSubfolderType,
  FileSource,
} from '@/generated/graphql.ts';

import FileViewer from '@/components/FileViewer.vue';
import { sentry } from '@/helpers/sentry.ts';
import { useFilesActions } from '@/composables/useFilesActions.ts';
import {
  useCompanySubFolderPeriodFilesStore,
} from '@/components/TableCompanySubFolderPeriodFiles/useCompanySubFolderPeriodFilesStore.ts';
import { isMeritSendActionHidden, isPurchaseInvoiceRow, usesPurchaseInvoiceMutation } from '@/components/MeritIntegation/helpers.ts';
import { useTicketsComposable } from '@/components/TicketsAccountingPeriod/useTicketsComposable.ts';
import { useTicketsPermissions } from '@/components/TicketsAccountingPeriod/useTicketsPermissions.ts';

interface FileAction {
  id: string;
  icon: string;
  tooltip: string;
  action: () => void;
  hotKey?: string;
  disabledState?: boolean;
}

// DIALOG
defineEmits([...useDialogPluginComponent.emits]);
const props = withDefaults(defineProps<{
  subfolder: PeriodsSubfolderType;
  isTicketCreationAvailable?: boolean;
}>(), {
  isTicketCreationAvailable: false,
});

const companySubFolderStore = useCompanySubFolderPeriodFilesStore();
const { periodSubfoldersFiles, currentOpenedFileId } = storeToRefs(companySubFolderStore);
const { confirmAccountingFile } = useFileStatusChange();
const bus = inject<EventBus>('bus');
const { dialogRef: viewAccountingFileDialogRef, onDialogHide } = useDialogPluginComponent();
const { openRenameDialog, download, quickMoveFile, isLoadingQuickMove, openDenyFileComponentDialog } = useFilesActions();
const { canSendToMerit, fetchMeritItemsFromEnty, sendInvoiceToMerit, sendChargebeeInvoiceToMerit, sendPurchaseInvoiceToMerit } = useMeritIntegration();
const { handleOpenDialogAddTicket } = useTicketsComposable();
const { canManageTickets, ticketAccessTooltip } = useTicketsPermissions();
const currentIndex = ref(0);

// Use computed properties to directly reference the file in periodSubfoldersFiles
const currentFile = computed<AccountingFileType | undefined>(() => {
  if (!periodSubfoldersFiles.value || !Array.isArray(periodSubfoldersFiles.value)) {
    return undefined;
  }

  if (currentIndex.value < 0 || currentIndex.value >= periodSubfoldersFiles.value.length) {
    return undefined;
  }

  return periodSubfoldersFiles.value[currentIndex.value];
});

const currentFileName = computed<string>(() => currentFile.value?.name || '');

const isSmallScreen = breakpoints.smaller('small');

const isQuickMoveAvailable = computed(() => props.subfolder?.type === AccountingReportPartTypeEnum.Default);

const isSendingToMerit = ref(false);

// Watch for changes in the currentOpenedFileId and update the displayed file
// This is another place where we use currentOpenedFileId to find the file in periodSubfoldersFiles
watch(currentOpenedFileId, (newFileId) => {
  if (!newFileId || !periodSubfoldersFiles.value || !Array.isArray(periodSubfoldersFiles.value)) {
    return;
  }

  const fileIndex = periodSubfoldersFiles.value.findIndex(file => file.id === newFileId);
  if (fileIndex === -1) {
    console.error(`File with id ${newFileId} not found in periodSubfoldersFiles`);
    return;
  }

  if (currentIndex.value !== fileIndex) {
    currentIndex.value = fileIndex;
  }
});

const openCreateTicketDialog = () => {
  if (!currentFile.value) return;

  handleOpenDialogAddTicket(
    props.subfolder.periodsFolder?.companyId || '',
    props.subfolder.periodsFolder?.yearFolder?.id || '',
    props.subfolder.periodsFolderId || '',
    props.subfolder.periodsFolder?.name || '',
    [{ id: currentFile.value.id, name: currentFile.value.name }],
  );
};

watch(periodSubfoldersFiles, (newFiles) => {
  if (!newFiles || !Array.isArray(newFiles) || !currentOpenedFileId.value) {
    return;
  }

  const fileIndex = newFiles.findIndex(file => file.id === currentOpenedFileId.value);
  if (fileIndex === -1) {
    console.error(`File with id ${currentOpenedFileId.value} not found in updated periodSubfoldersFiles`);
    return;
  }

  if (currentIndex.value !== fileIndex) {
    currentIndex.value = fileIndex;
  }
}, { deep: true });

const isDialogFocused = ref(true);

const isFileConfirmed = computed(() =>
  currentFile.value?.accStatusId === FileAccStatus.Confirmed,
);

const isFileDenied = computed(() =>
  currentFile.value?.accStatusId === FileAccStatus.Denied,
);

const rejectionReasonText = computed(() =>
  isFileDenied.value ? getRejectionReasonText(currentFile.value?.rejectionReasonId) : '',
);

const showRejectionCommentIcon = computed(() =>
  isFileDenied.value && !!currentFile.value?.rejectionComment,
);

const setFocused = (value: boolean) => {
  isDialogFocused.value = value;
};

const openEditModal = () => {
  setFocused(false);
  if (!currentFile.value) return;
  openRenameDialog(currentFileName.value, currentFile.value.id, () => {
    setFocused(true);
  }).then(() => {
    callBusEventAfterChanges();
  });
};

// QUICK MOVE
const quickMove = async (typeFolder: typesSubfoldersForQuickMove) => {
  if (!isQuickMoveAvailable.value) {
    return;
  }
  try {
    await moveFile(typeFolder);
    callBusEventAfterChanges();
  } catch (error) {
    sentry().sendError(error as Error);
  }
};

const moveFile = async (typeFolder: typesSubfoldersForQuickMove) => {
  if (!props.subfolder.periodsFolderId || !currentFile.value) {
    return;
  }
  await quickMoveFile(props.subfolder.periodsFolderId, typeFolder, currentFile.value.id);
  callBusEventAfterChanges();
};

// CHANGE DOCUMENT

const changeDocument = (direction: 'next' | 'prev') => {
  if (!periodSubfoldersFiles.value || !Array.isArray(periodSubfoldersFiles.value)) {
    return;
  }

  const indexChange = direction === 'next' ? 1 : -1;
  const newIndex = currentIndex.value + indexChange;

  if (newIndex >= 0 && newIndex < periodSubfoldersFiles.value.length) {
    currentIndex.value = newIndex;
    // Update the currentOpenedFileId in the store
    if (currentFile.value) {
      companySubFolderStore.setCurrentOpenedFileId(currentFile.value.id);
    }
  }
};

const downloadFile = () => {
  if (currentFile.value) {
    download(currentFile.value.id, currentFileName.value);
  }
};

const initializeLocalState = () => {
  if (!periodSubfoldersFiles.value || !Array.isArray(periodSubfoldersFiles.value)) {
    console.error('ViewAccountingFileDialog: periodSubfoldersFiles is not an array or is empty');
    return;
  }

  if (!currentOpenedFileId.value) {
    console.error('ViewAccountingFileDialog: currentOpenedFileId is not set');
    return;
  }

  const fileIndex = periodSubfoldersFiles.value.findIndex(file => file.id === currentOpenedFileId.value);
  if (fileIndex === -1) {
    return;
  }

  currentIndex.value = fileIndex;
};

const callBusEventAfterChanges = () => {
  if (!bus || !currentFile.value) return;
  bus.emit(EventBusEvents.changedAccountingFiles, currentFile.value.id);
};

const confirmFileStatus = async () => {
  if (!currentFile.value?.id) return;
  try {
    const success = await confirmAccountingFile([currentFile.value.id]);
    if (success) {
      callBusEventAfterChanges();
    }
  } catch (error) {
    console.error(error);
  }
};

const denyAccountingFile = () => {
  if (!currentFile.value) return;
  setFocused(false);
  const onCloseCallback = () => {
    setFocused(true);
  };
  openDenyFileComponentDialog(currentFile.value, undefined, onCloseCallback);
};

const getMeritTooltipText = (): string =>
  isSendToMeritDisabledByConfig.value ? 'Configure Merit settings' : 'Send to merit';

// Merit item codes are only required for the sales invoice flow;
// purchase invoices (Monit INVOICE) don't depend on them
const isSendToMeritDisabledByConfig = computed(() =>
  !canSendToMerit() && !isPurchaseInvoiceRow(currentFile.value),
);

const handleSendToMerit = async () => {
  if (!currentFile.value || !props.subfolder?.periodsFolder?.companyId) return;

  isSendingToMerit.value = true;
  try {
    const success = await sendInvoiceToMerit(
      props.subfolder.periodsFolder.companyId,
      currentFile.value.id,
      currentFile.value.customerVatNumber || undefined,
    );
    if (success) {
      Notify.create({
        type: 'positive',
        message: 'Invoice successfully sent to Merit',
        position: 'top',
      });
      callBusEventAfterChanges();
    }
  } catch (error) {
    console.error('Error sending invoice to Merit:', error);
  } finally {
    isSendingToMerit.value = false;
  }
};

const handleSendChargebeeInvoiceForCurrentFile = async () => {
  if (!currentFile.value) return;
  try {
    isSendingToMerit.value = true;
    const ok = await sendChargebeeInvoiceToMerit(currentFile.value.id);
    if (ok) {
      callBusEventAfterChanges();
    }
  } catch (error) {
    console.error('Error sending Chargebee invoice to Merit:', error);
  } finally {
    isSendingToMerit.value = false;
  }
};

// - Chargebee → use sendChargebeeInvoiceToMerit (with validation dialog flow inside)
// - Monite INVOICE and credit notes recognized by file name → use sendPurchaseInvoiceToMerit
// - Invoicing/other → use handleSendToMerit (existing flow)
const handleSendToMeritBySource = async () => {
  if (!currentFile.value) return;

  if (currentFile.value.source === FileSource.Chargebee) {
    await handleSendChargebeeInvoiceForCurrentFile();
    return;
  }

  if (usesPurchaseInvoiceMutation(currentFile.value)) {
    await handleSendPurchaseInvoiceForCurrentFile();
    return;
  }

  await handleSendToMerit();
};

const handleSendPurchaseInvoiceForCurrentFile = async () => {
  if (!currentFile.value) return;
  isSendingToMerit.value = true;
  try {
    const ok = await sendPurchaseInvoiceToMerit(currentFile.value.id);
    if (ok) {
      callBusEventAfterChanges();
    }
  } finally {
    isSendingToMerit.value = false;
  }
};

const fileActions = computed<FileAction[]>(() => {
  const actions: FileAction[] = [
    {
      id: 'confirm',
      icon: 'check',
      tooltip: 'Confirm',
      action: confirmFileStatus,
      disabledState: isFileConfirmed.value,
    },
    {
      id: 'deny',
      icon: 'cancel',
      tooltip: 'Deny',
      action: denyAccountingFile,
    },
    {
      id: 'rename',
      icon: 'drive_file_rename_outline',
      tooltip: 'Rename',
      action: openEditModal,
      hotKey: getHotKeyLabel(HotKeyActionId.Rename),
    },
    {
      id: 'download',
      icon: 'download',
      tooltip: 'Download',
      action: downloadFile,
    },
  ];

  if (props.isTicketCreationAvailable) {
    actions.push({
      id: 'createQuestion',
      icon: 'forum',
      tooltip: ticketAccessTooltip.value || 'Create a question',
      action: openCreateTicketDialog,
      disabledState: !canManageTickets.value,
    });
  }

  // Nothing to offer once the file is in Merit: the status chip above says so
  if (!isMeritSendActionHidden(currentFile.value, props.subfolder?.type)) {
    actions.push({
      id: 'sendToMerit',
      icon: 'send',
      tooltip: getMeritTooltipText(),
      action: handleSendToMeritBySource,
      disabledState: isSendingToMerit.value || isSendToMeritDisabledByConfig.value,
    });
  }

  return actions;
});

const getHotKeyLabel = (id: string) => {
  const hotKey = FILE_DIALOG_HOT_KEYS.find(hotKey => hotKey.id === id);
  return hotKey ? hotKey.label : '';
};

const registerHotKeys = () => {
  hotKeys.registerHandler({
    actionId: HotKeyActionId.Rename,
    handler: () => {
      if (isDialogFocused.value) {
        openEditModal();
      }
    },
  });

  hotKeys.registerHandler({
    actionId: HotKeyActionId.ArrowLeft,
    handler: () => {
      if (isDialogFocused.value) {
        changeDocument('prev');
      }
    },
  });

  hotKeys.registerHandler({
    actionId: HotKeyActionId.ArrowRight,
    handler: () => {
      if (isDialogFocused.value) {
        changeDocument('next');
      }
    },
  });

  if (isQuickMoveAvailable.value) {
    hotKeys.registerHandler({
      actionId: HotKeyActionId.MoveToIncoming,
      handler: () => {
        if (isDialogFocused.value) {
          quickMove(AccountingReportPartTypeEnum.Incoming);
        }
      },
    });

    hotKeys.registerHandler({
      actionId: HotKeyActionId.MoveToOutgoing,
      handler: () => {
        if (isDialogFocused.value) {
          quickMove(AccountingReportPartTypeEnum.Outgoing);
        }
      },
    });
  }
};

// HOT KEYS
const hotKeys = useHotKeys();

onMounted(async () => {
  initializeLocalState();
  registerHotKeys();
  hotKeys.setupHotKeys();
  if (props.subfolder?.periodsFolder?.companyId) {
    await fetchMeritItemsFromEnty(props.subfolder.periodsFolder.companyId);
  }
});

onBeforeUnmount(() => {
  hotKeys.cleanupHotKeys();
  companySubFolderStore.setCurrentOpenedFileId('');
});
</script>

<template>
  <q-dialog
    ref="viewAccountingFileDialogRef"
    full-height
    class="view-file-dialog-wrapper"
    @show="() => setFocused(true)"
    @hide="() => setFocused(false)"
    @shake="() => setFocused(true)"
  >
    <div class="view-file-dialog row items-center justify-center no-wrap">
      <q-btn
        v-if="!isSmallScreen"
        round
        icon="arrow_left"
        color="teal-1"
        text-color="blue-grey-10"
        class="view-file-dialog__doc-btn q-mr-md"
        :disable="currentIndex === 0"
        @click.stop="changeDocument('prev')"
      />
      <q-card class="view-file-dialog__content">
        <q-card-section>
          <div class="row full-width items-center no-wrap q-mb-md">
            <div class="col row no-wrap items-center">
              <div
                class="ellipsis cursor-pointer"
                style="max-width: calc(100% - 80px);"
                @click="openEditModal"
              >
                {{ currentFileName }}
              </div>
              <q-btn
                round
                flat
                size="sm"
                icon="content_copy"
                color="primary"
                class="q-ml-xs"
                @click.stop="copyToClipboardWithNotification(currentFileName)"
              />
              <q-tooltip class="text-body2">
                {{ currentFileName }}
              </q-tooltip>
            </div>
            <q-btn
              flat
              round
              dense
              size="md"
              color="primary"
              icon="close"
              class="q-ml-sm"
              @click="onDialogHide"
            />
          </div>
          <q-banner
            rounded
            dense
            class="bg-grey-1"
          >
            <div class="row items-center justify-between">
              <div class="row items-center">
                <span class="text-primary">Status:</span>
                <div class="row items-center no-wrap">
                  <span
                    :class="getAccountingStatusColorClass(currentFile?.accStatusId)"
                    class="q-ml-xs"
                  >
                    {{ getAccountingStatusText(currentFile?.accStatusId) }}
                  </span>
                  <q-chip
                    v-if="rejectionReasonText"
                    dense
                    square
                    size="12px"
                    color="red-1"
                    text-color="red-9"
                    class="q-ma-none q-ml-sm"
                  >
                    {{ rejectionReasonText }}
                  </q-chip>
                  <q-icon
                    v-if="showRejectionCommentIcon"
                    name="chat_bubble_outline"
                    color="grey-7"
                    size="16px"
                    class="q-ml-xs cursor-pointer"
                  >
                    <q-tooltip
                      max-width="320px"
                      class="text-body2 bg-dark text-grey-2"
                    >
                      {{ currentFile?.rejectionComment }}
                    </q-tooltip>
                  </q-icon>
                </div>
                <div class="q-ml-md">
                  <MeritStatusChip
                    :row="currentFile"
                    :subfolder-type="props.subfolder?.type"
                  />
                </div>
                <div
                  v-if="currentFile?.meritRefNo"
                  class="q-ml-md cursor-pointer"
                  @click.stop="copyToClipboardWithNotification(currentFile.meritRefNo)"
                >
                  <span class="text-primary">Merit Reference:</span> {{ currentFile.meritRefNo }}
                  <q-tooltip class="text-body2">
                    Click to copy reference number
                  </q-tooltip>
                </div>
              </div>
              <div class="flex flex-center justify-center q-gutter-xs">
                <div
                  v-if="isQuickMoveAvailable"
                  class="q-mr-lg"
                >
                  <q-btn
                    label="In"
                    color="primary"
                    no-caps
                    flat
                    size="md"
                    :disable="isLoadingQuickMove"
                    @click.prevent="quickMove(AccountingReportPartTypeEnum.Incoming)"
                  >
                    <q-tooltip
                      class="text-body2"
                    >
                      Move to incoming {{ getHotKeyLabel(HotKeyActionId.MoveToIncoming) }}
                    </q-tooltip>
                  </q-btn>
                  <q-btn
                    label="Out"
                    color="primary"
                    no-caps
                    flat
                    size="md"
                    :disable="isLoadingQuickMove"
                    @click.prevent="quickMove(AccountingReportPartTypeEnum.Outgoing)"
                  >
                    <q-tooltip
                      class="text-body2"
                    >
                      Move to outgoing {{ getHotKeyLabel(HotKeyActionId.MoveToOutgoing) }}
                    </q-tooltip>
                  </q-btn>
                </div>
                <q-btn
                  v-for="action in fileActions"
                  :key="action.id"
                  flat
                  round
                  dense
                  size="md"
                  color="primary"
                  :icon="action.icon"
                  :disable="action.disabledState"
                  :loading="action.id === 'sendToMerit' && isSendingToMerit"
                  @click="action.action"
                >
                  <q-tooltip class="text-body2">
                    {{ action.tooltip }} {{ action.hotKey || '' }}
                  </q-tooltip>
                </q-btn>
              </div>
            </div>
          </q-banner>
        </q-card-section>

        <q-card-section class="q-pt-none view-file-dialog__section_body">
          <FileViewer
            :file-id="currentFile?.id"
            :file-name="currentFile?.name"
          />
        </q-card-section>
      </q-card>
      <q-btn
        v-if="!isSmallScreen"
        round
        icon="arrow_right"
        color="teal-1"
        text-color="blue-grey-10"
        class="view-file-dialog__doc-btn q-ml-md"
        :disable="!periodSubfoldersFiles || !Array.isArray(periodSubfoldersFiles) || currentIndex === (periodSubfoldersFiles as AccountingFileType[]).length - 1"
        @click.stop="changeDocument('next')"
      />
    </div>
  </q-dialog>
</template>

<style lang="scss">
.view-file-dialog-wrapper {
  overflow: hidden;
  width: 100%;
  height: 100%;
}
.view-file-dialog {
  width: 80%;
  max-width: 80% !important;
  height: 100%;

  @media screen and (max-width: 780px) {
    width: 100%;
    max-width: 100% !important;

    &__content {
      width: 100% !important;
    }
  }

  &__content {
    width: 80%;
    height: 100%;
    display: flex;
    flex-direction: column;
  }

  &__section_body {
    height: 90%;
  }

  .input-editable {
    width: 200px;
  }

  .justify-self-end {
    justify-self: end;
  }
}

.view-file-dialog__section_body {
  overflow: auto;
}

/* Фолбэк для вертикального центрирования стрелок */
.view-file-dialog__doc-btn {
  position: sticky;
  top: 50%;
  transform: translateY(-50%);
  z-index: 2;
}
</style>
